import { HOLD_MS } from './gestures/gesture'

/** Gesture arbitration is independent of React so timing, scrolling and cancellation are testable. */
export interface ReorderDrag {
  activeIndex: number
  overIndex: number
  offsetY: number
  rowHeight: number
}
interface Point { x: number; y: number }
interface Geometry { rowHeight: number; scrollTop: number; top: number; bottom: number }
interface Options {
  count: number
  geometry: () => Geometry
  scrollBy: (distance: number) => void
  onActivate: () => void
  onDrag: (drag: ReorderDrag | null) => void
  onCommit: (from: number, to: number) => void
}
export const REORDER_HOLD_MS = HOLD_MS
const MOVE_TOLERANCE = 8
const EDGE = 48

export function createReorderGesture(options: Options) {
  let session: { index: number; origin: Point; point: Point; scrollTop: number; active: boolean; over: number } | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let frame: number | null = null

  function cancel() {
    if (timer !== null) clearTimeout(timer)
    if (frame !== null) cancelAnimationFrame(frame)
    timer = null
    frame = null
    const wasActive = session?.active
    session = null
    if (wasActive) options.onDrag(null)
  }

  function update() {
    if (!session?.active) return
    const geometry = options.geometry()
    const height = Math.max(1, geometry.rowHeight)
    const offset = session.point.y - session.origin.y + geometry.scrollTop - session.scrollTop
    const offsetY = Math.max(-session.index * height, Math.min((options.count - session.index - 1) * height, offset))
    session.over = Math.max(0, Math.min(options.count - 1, session.index + Math.round(offsetY / height)))
    options.onDrag({ activeIndex: session.index, overIndex: session.over, offsetY, rowHeight: height })
  }

  function tick() {
    if (!session?.active) return
    const { top, bottom, scrollTop } = options.geometry()
    const above = session.point.y - top
    const below = bottom - session.point.y
    const speed = above < EDGE ? -Math.min(12, Math.ceil((EDGE - above) / 4))
      : below < EDGE ? Math.min(12, Math.ceil((EDGE - below) / 4)) : 0
    if (speed) {
      options.scrollBy(speed)
      if (options.geometry().scrollTop !== scrollTop) update()
    }
    frame = requestAnimationFrame(tick)
  }

  return {
    lift(index: number, point: Point) {
      cancel()
      session = { index, origin: point, point, scrollTop: options.geometry().scrollTop, active: true, over: index }
      options.onActivate()
      update()
      frame = requestAnimationFrame(tick)
    },
    start(index: number, point: Point) {
      cancel()
      if (options.count < 2 || index < 0 || index >= options.count) return
      session = { index, origin: point, point, scrollTop: options.geometry().scrollTop, active: false, over: index }
      timer = setTimeout(() => {
        timer = null
        if (!session) return
        session.active = true
        options.onActivate()
        update()
        frame = requestAnimationFrame(tick)
      }, REORDER_HOLD_MS)
    },
    /** False leaves ordinary scrolling to the browser. */
    move(point: Point) {
      if (!session) return false
      if (!session.active && Math.hypot(point.x - session.origin.x, point.y - session.origin.y) > MOVE_TOLERANCE) {
        cancel()
        return false
      }
      session.point = point
      if (!session.active) return false
      update()
      return true
    },
    drop() {
      const finished = session
      cancel()
      if (finished?.active && finished.index !== finished.over) options.onCommit(finished.index, finished.over)
    },
    cancel,
    get pending() { return session !== null },
    get active() { return session?.active ?? false },
  }
}
